import { QCONFIG_DETAIL_DATA_TEST_ID } from '@e2e/constants/exam-management';
import { QConfigStatusTag } from '@exam-management/components';
import {
  GetQConfigByIdDocument,
  QConfigStatus,
  useDisableQConfigMutation,
} from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { QConfigDuplicationModal } from '@exam-management/qconfig-duplication';
import { QConfigEditModal } from '@exam-management/qconfig-edit';
import { useParams } from '@exam-management/router';
import {
  CircleIcon,
  ConfirmationModal,
  ContentCopyRoundedIcon,
  DeleteOutlineIcon,
  Divider,
  EditOutlinedIcon,
  Flex,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  MoreVertIcon,
  styled,
  Text,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { usePermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';

import { DeleteQConfigModal } from '../delete-qconfig-modal';
import { PublishQConfigButton } from '../publish-qconfig-button';

interface QConfigDetailHeaderButtonGroupProps {
  status: QConfigStatus;
  qConfigId: string;
  name: string;
  totalQuestions: number;
}

const StyledMenuSection = styled(Flex)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
  padding: theme.spacing(2),
}));

export default function QConfigDetailHeaderButtonGroup(
  props: Readonly<QConfigDetailHeaderButtonGroupProps>,
) {
  const { status, qConfigId, name, totalQuestions } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = anchorEl !== null;

  const [isDuplicationModalOpen, { open: openDuplicationModal, close: closeDuplicationModal }] =
    useDisclosure(false);

  const [
    isDeleteConfirmationModalOpen,
    { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal },
  ] = useDisclosure(false);

  const [
    isDisabledConfirmationModalOpen,
    { open: openDisabledConfirmationModal, close: closeDisabledConfirmationModal },
  ] = useDisclosure(false);
  const [isEditModalOpen, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);

  const { qconfigId } = useParams('/exam-courses/:courseId/qconfigs/:qconfigId');
  const { showSnackbar } = useSnackbar();
  const { permissions } = usePermission();
  const hasChangeStatusPermission = permissions.includes(APP_PERMISSIONS.EXAM.QCONFIG.STATUS);

  const [disableQConfig, { loading: isDisableQConfigLoading }] = useDisableQConfigMutation({
    onCompleted: () => {
      showSnackbar({
        message: 'QConfig has been disabled',
        variant: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        message: 'Unable to Disabled QConfig',
        variant: 'error',
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQConfigByIdDocument,
        variables: {
          getQConfigByIdInput: {
            qConfigId,
          },
        },
      },
    ],
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeStatusToDisabled = async () => {
    await disableQConfig({ variables: { disableQConfigInput: { qConfigId: qConfigId } } });
    handleClose();
    closeDisabledConfirmationModal();
  };

  //TODO: Add action menu options
  const baseActionMenuOptions = [
    {
      label: 'Edit Information',
      IconComponent: EditOutlinedIcon,
      action: () => openEditModal(),
      requiredPermission: [APP_PERMISSIONS.EXAM.QCONFIG.UPDATE],
      status: [QConfigStatus.Draft],
    },
    {
      label: 'Duplicate',
      IconComponent: ContentCopyRoundedIcon,
      action: () => openDuplicationModal(),
      requiredPermission: [APP_PERMISSIONS.EXAM.QCONFIG.CREATE],
      status: [QConfigStatus.Draft, QConfigStatus.Published, QConfigStatus.Disabled],
    },
    {
      label: 'Delete',
      IconComponent: DeleteOutlineIcon,
      variant: 'error',
      action: () => openDeleteConfirmationModal(),
      requiredPermission: [APP_PERMISSIONS.EXAM.QCONFIG.DELETE],
      status: [QConfigStatus.Draft],
    },
  ];

  const actionMenuOptions = baseActionMenuOptions.filter((menu) => {
    return (
      menu.requiredPermission.every((permission) => permissions.includes(permission)) &&
      menu.status.includes(status)
    );
  });

  return (
    <Flex sx={{ gap: (theme) => theme.spacing(4), alignItems: 'center' }}>
      <>
        <QConfigStatusTag status={status} data-testid={QCONFIG_DETAIL_DATA_TEST_ID.STATUS_TAG} />
        <Divider orientation='vertical' variant='middle' flexItem />
        {status === QConfigStatus.Draft && hasChangeStatusPermission ? (
          <PublishQConfigButton />
        ) : null}
        <IconButton
          onClick={handleClick}
          data-testid={QCONFIG_DETAIL_DATA_TEST_ID.HEADER_MENU}
          sx={(theme) => ({
            border: '1px solid',
            borderColor: theme.palette.grey[400],
            borderRadius: theme.shape.rounded(1),
          })}
        >
          <MoreVertIcon />
        </IconButton>
      </>
      <Menu
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          mt: (theme) => theme.spacing(0.5),
        }}
        MenuListProps={{
          sx: (theme) => ({
            borderRadius: theme.shape.rounded(2),
            width: '180px',
            p: theme.spacing(0),
          }),
        }}
      >
        {status === QConfigStatus.Published && hasChangeStatusPermission ? (
          <>
            <StyledMenuSection>
              <Text
                variant='captionSemibold'
                sx={(theme) => ({
                  px: theme.spacing(2),
                  color: theme.palette.text.secondary,
                })}
              >
                Update Status to
              </Text>
              <MenuItem
                onClick={() => openDisabledConfirmationModal()}
                sx={{
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.primary.lighter,
                  },
                }}
              >
                <CircleIcon
                  sx={(theme) => ({
                    color: theme.palette.grey[500],
                    width: '10px',
                    height: '10px',
                    mr: theme.spacing(2),
                  })}
                />
                <ListItemText>Disabled</ListItemText>
              </MenuItem>
            </StyledMenuSection>
            <Divider />
          </>
        ) : null}
        <StyledMenuSection>
          <Text
            variant='captionSemibold'
            sx={(theme) => ({
              px: theme.spacing(2),
              color: theme.palette.text.secondary,
            })}
          >
            Actions
          </Text>
          {actionMenuOptions.map(({ label, IconComponent, action, variant }) => (
            <MenuItem
              key={label}
              onClick={() => {
                handleClose();
                action();
              }}
              sx={{
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.primary.lighter,
                },
              }}
            >
              <IconComponent
                sx={(theme) => ({
                  width: '24px',
                  height: '24px',
                  mr: theme.spacing(2),
                  color: variant === 'error' ? theme.palette.error.main : theme.palette.grey[600],
                })}
              />
              <ListItemText
                sx={(theme) => ({
                  color: variant === 'error' ? theme.palette.error.main : undefined,
                })}
              >
                {label}
              </ListItemText>
            </MenuItem>
          ))}
        </StyledMenuSection>
      </Menu>
      <ConfirmationModal
        title='Disable QConfig?'
        content={
          <>
            เมื่อทำการ Disable แล้ว{' '}
            <Text component='span' variant='body1Semibold'>
              จะไม่สามารถสร้างชุดข้อสอบใหม่จาก QConfig นี้ได้อีก
            </Text>{' '}
            แต่ชุดข้อสอบที่สร้างไว้ก่อนหน้านี้จะไม่ได้รับผลกระทบ
          </>
        }
        onDiscard={closeDisabledConfirmationModal}
        acceptText='Disable'
        open={isDisabledConfirmationModalOpen}
        onClose={closeDisabledConfirmationModal}
        loading={isDisableQConfigLoading}
        onAccept={handleChangeStatusToDisabled}
      />
      <DeleteQConfigModal
        onClose={closeDeleteConfirmationModal}
        open={isDeleteConfirmationModalOpen}
        qconfigId={qconfigId}
      />

      {isDuplicationModalOpen ? (
        <QConfigDuplicationModal
          onClose={closeDuplicationModal}
          open={isDuplicationModalOpen}
          qConfigId={qconfigId}
          name={name}
        />
      ) : null}
      {isEditModalOpen ? (
        <QConfigEditModal
          onClose={closeEditModal}
          open={isEditModalOpen}
          qConfigId={qconfigId}
          name={name}
          totalQuestions={totalQuestions}
        />
      ) : null}
    </Flex>
  );
}
